import type { InspectionTemplate, TemplateSection } from 'inspection-types';

export type ReorderSectionsChange = {
  kind: 'reorderSections';
  sectionIds: string[];
};

export type ReorderQuestionsChange = {
  kind: 'reorderQuestions';
  sectionId: string;
  itemIds: string[];
};

export type ReorderChange = ReorderSectionsChange | ReorderQuestionsChange;

export type TemplateChange =
  | { kind: 'hideSection'; sectionId: string }
  | { kind: 'unhideSection'; sectionId: string }
  | { kind: 'hideQuestion'; itemId: string }
  | { kind: 'unhideQuestion'; itemId: string }
  | ReorderChange;

type Orderable = {
  id: string;
  order: number;
};

type SectionRemovedChange = {
  sectionId: string;
  removed: boolean;
};

type ItemRemovedChange = {
  itemId: string;
  removed: boolean;
};

function setSectionRemoved(
  template: InspectionTemplate,
  { sectionId, removed }: SectionRemovedChange
): InspectionTemplate {
  const sections = template.sections.map(section =>
    section.id === sectionId ? { ...section, removed } : section
  );

  return { ...template, sections };
}

function setItemRemoved(
  section: TemplateSection,
  { itemId, removed }: ItemRemovedChange
): TemplateSection {
  const hasItem = section.items.some(item => item.id === itemId);

  if (!hasItem) {
    return section;
  }

  const items = section.items.map(item =>
    item.id === itemId ? { ...item, removed } : item
  );

  return { ...section, items };
}

function setQuestionRemoved(
  template: InspectionTemplate,
  change: ItemRemovedChange
): InspectionTemplate {
  const sections = template.sections.map(section =>
    setItemRemoved(section, change)
  );

  return { ...template, sections };
}

// Hidden nodes keep their place in the sequence. A node the sequence leaves out goes after
// the named ones, in its current order.
function renumberBySequence<T extends Orderable>(
  nodes: T[],
  sequence: readonly string[]
): T[] {
  const positionById = new Map<string, number>();

  sequence.forEach(id => {
    if (!positionById.has(id)) {
      positionById.set(id, positionById.size);
    }
  });

  const rank = (node: T) => positionById.get(node.id) ?? positionById.size;
  const sequenced = [...nodes].sort(
    (a, b) => rank(a) - rank(b) || a.order - b.order
  );

  return sequenced.map((node, order) =>
    node.order === order ? node : { ...node, order }
  );
}

function reorderSections(
  template: InspectionTemplate,
  { sectionIds }: ReorderSectionsChange
): InspectionTemplate {
  return {
    ...template,
    sections: renumberBySequence(template.sections, sectionIds),
  };
}

function reorderQuestions(
  template: InspectionTemplate,
  { sectionId, itemIds }: ReorderQuestionsChange
): InspectionTemplate {
  const sections = template.sections.map(section =>
    section.id === sectionId
      ? { ...section, items: renumberBySequence(section.items, itemIds) }
      : section
  );

  return { ...template, sections };
}

export function applyChange(
  template: InspectionTemplate,
  change: TemplateChange
): InspectionTemplate {
  switch (change.kind) {
    case 'hideSection':
      return setSectionRemoved(template, {
        sectionId: change.sectionId,
        removed: true,
      });
    case 'unhideSection':
      return setSectionRemoved(template, {
        sectionId: change.sectionId,
        removed: false,
      });
    case 'hideQuestion':
      return setQuestionRemoved(template, {
        itemId: change.itemId,
        removed: true,
      });
    case 'unhideQuestion':
      return setQuestionRemoved(template, {
        itemId: change.itemId,
        removed: false,
      });
    case 'reorderSections':
      return reorderSections(template, change);
    case 'reorderQuestions':
      return reorderQuestions(template, change);
  }
}

export function applyChanges(
  template: InspectionTemplate,
  changes: readonly TemplateChange[]
): InspectionTemplate {
  return changes.reduce(applyChange, template);
}
